import type { EmojiItem, EmojiLine, UserEmoji } from "../model";
import {
    EMOJI_CATEGORY,
    EMOJI_GROUPS,
    EMOJI_PANEL_LINE_HEIGHT,
    EMOJI_PANEL_TITLE_HEIGHT,
} from "./constants";
import { EMOJI_ITEMS } from "./emojiItems";

const EMOJI_COUNT_IN_LINE = 8;

// Кэшируем массив эмодзи для избежания создания нового массива при каждом поиске
const EMOJI_ITEM_ARRAY = Object.values(EMOJI_ITEMS);

const emojiSearchFn = (emoji: EmojiItem, searchQuery: string) => {
    const clearSearchQuery = searchQuery.trim().toLowerCase();

    return (
        !clearSearchQuery ||
        emoji.description.toLowerCase().includes(clearSearchQuery) ||
        emoji.descriptionLocal?.toLowerCase()?.includes(clearSearchQuery) ||
        emoji.keywords?.some((code) => code.toLowerCase().includes(clearSearchQuery)) ||
        emoji.keywordsLocal?.some((code) => code.toLowerCase().includes(clearSearchQuery))
    );
};

const getGroupEmojiList = (emojiList: EmojiItem[], groupName: string) => {
    return emojiList.filter((emoji) => groupName === EMOJI_CATEGORY[emoji.category]?.nameLocal);
};

const splitEmojiByLines = (array: EmojiItem[], size: number): EmojiItem[][] => {
    const result: EmojiItem[][] = [];
    for (let i = 0; i < array.length; i += size) {
        result.push(array.slice(i, i + size));
    }
    return result;
};

export const filterEmojiList = (searchQuery: string) => {
    return EMOJI_ITEM_ARRAY.filter((emoji) => emojiSearchFn(emoji, searchQuery));
};

type GetEmojiPanelLinesParams = {
    staticEmojiList: EmojiItem[];
    searchQuery: string;
    size: "s" | "m";
};

export const getEmojiPanelLines = ({
    searchQuery,
    size,
    staticEmojiList,
}: GetEmojiPanelLinesParams) => {
    const emojiList = filterEmojiList(searchQuery);

    return EMOJI_GROUPS.reduce<EmojiLine[]>((acc, group) => {
        // при поиске, раздел "Часто используемые" не отображается
        if (group.static && searchQuery) return acc;
        // при пустом массиве staticEmojiList, раздел "Часто используемые" не отображается
        if (group.static && !staticEmojiList.length) return acc;

        const groupEmojiList = group.static
            ? staticEmojiList
            : getGroupEmojiList(emojiList, group.label);

        if (groupEmojiList.length) {
            acc.push({
                type: "title",
                key: group.label,
                group: group.label,
                value: group.label,
                height: EMOJI_PANEL_TITLE_HEIGHT[size],
            });

            splitEmojiByLines(groupEmojiList, EMOJI_COUNT_IN_LINE).forEach(
                (emojiPanelLine, index) => {
                    acc.push({
                        type: "emojiList",
                        value: emojiPanelLine,
                        key: group.label + index,
                        group: group.label,
                        height: EMOJI_PANEL_LINE_HEIGHT[size],
                    });
                },
            );
        }

        return acc;
    }, []);
};

export const makeEmojiItemFromUserEmoji = (emojiList: UserEmoji[]): EmojiItem[] => {
    return emojiList.reduce<EmojiItem[]>((acc, emoji) => {
        const emojiItem = EMOJI_ITEMS[emoji.emojiId];
        if (emojiItem) {
            acc.push(emojiItem);
        }
        return acc;
    }, []);
};

export const getEmojiGroupIndexList = (emojiList: EmojiLine[]): Record<string, number> => {
    return emojiList.reduce<Record<string, number>>((acc, emojiLine, index) => {
        if (emojiLine.type === "title") {
            acc[emojiLine.group] = index;
        }

        return acc;
    }, {});
};

type GetActiveGroupParams = {
    rowList: EmojiLine[];
    range: {
        startIndex: number;
        endIndex: number;
    } | null;
};

export const getActiveGroup = ({ rowList, range }: GetActiveGroupParams) => {
    if (range) {
        const groupSet = new Set<string>([]);
        for (let i = range.startIndex + 1; i <= range.endIndex; i++) {
            groupSet.add(rowList[i].group);
        }

        return Array.from(groupSet).sort(
            (item1, item2) =>
                EMOJI_GROUPS.findIndex((item) => item.label === item1) -
                EMOJI_GROUPS.findIndex((item) => item.label === item2),
        )[0];
    }

    return EMOJI_GROUPS[0].label;
};

export const capitalize = (string: string) => {
    if (!string) return "";

    return string[0].toUpperCase() + string.slice(1);
};
